import { IChartOfAccount } from "../accounting.interface";

/**
 * Flattens a hierarchical chart of accounts structure into a flat array
 * @param accounts - Hierarchical array of accounts
 * @returns Flat array of all accounts
 */
export const flattenAccounts = (
  accounts: IChartOfAccount[]
): IChartOfAccount[] => {
  const flattened: IChartOfAccount[] = [];
  const traverse = (accs: IChartOfAccount[]) => {
    accs.forEach((acc) => {
      flattened.push(acc);
      if (acc.children) {
        traverse(acc.children);
      }
    });
  };
  traverse(accounts);
  return flattened;
};

/**
 * Updates an account in the hierarchical tree structure
 * @param accounts - Array of accounts to search in
 * @param accountId - ID of the account to update
 * @param updatedAccount - Updated account data
 * @returns Updated accounts array
 */
export const updateAccountInTree = (
  accounts: IChartOfAccount[],
  accountId: string,
  updatedAccount: IChartOfAccount
): IChartOfAccount[] => {
  return accounts.map((account) => {
    if (account.id === accountId) {
      return updatedAccount;
    }
    if (account.children) {
      return {
        ...account,
        children: updateAccountInTree(
          account.children,
          accountId,
          updatedAccount
        ),
      };
    }
    return account;
  });
};

/**
 * Adds a new account to the hierarchical tree structure
 * @param accounts - Existing accounts array
 * @param newAccount - New account to add
 * @returns Updated accounts array with new account
 */
export const addAccountToTree = (
  accounts: IChartOfAccount[],
  newAccount: IChartOfAccount
): IChartOfAccount[] => {
  if (!newAccount.parentAccountId) {
    // Add as root account
    return [...accounts, newAccount];
  }

  return accounts.map((account) => {
    if (account.id === newAccount.parentAccountId) {
      return {
        ...account,
        children: [...(account.children || []), newAccount],
      };
    }
    if (account.children) {
      return {
        ...account,
        children: addAccountToTree(account.children, newAccount),
      };
    }
    return account;
  });
};

/**
 * Calculates parent account balances from child accounts
 * @param parentAccount - Parent account to calculate balance for
 * @param allAccounts - Flat array of all accounts for reference
 * @returns Calculated opening and current balances
 */
export const calculateParentBalance = (
  parentAccount: IChartOfAccount,
  allAccounts: IChartOfAccount[]
): { openingBalance?: number; currentBalance?: number } => {
  if (parentAccount.allowDirectPosting || !parentAccount.children) {
    return {}; // Only calculate for parent accounts
  }

  let totalOpening = 0;
  let totalCurrent = 0;
  let hasOpeningBalances = false;
  let hasCurrentBalances = false;

  const calculateFromChildren = (children: IChartOfAccount[]) => {
    children.forEach((child) => {
      if (child.allowDirectPosting) {
        // Leaf account - add its balances
        if (child.openingBalance !== undefined) {
          totalOpening += child.openingBalance;
          hasOpeningBalances = true;
        }
        if (child.currentBalance !== undefined) {
          totalCurrent += child.currentBalance;
          hasCurrentBalances = true;
        }
      } else if (child.children) {
        // Parent account - recurse through its children
        calculateFromChildren(child.children);
      }
    });
  };

  calculateFromChildren(parentAccount.children);

  return {
    openingBalance: hasOpeningBalances ? totalOpening : undefined,
    currentBalance: hasCurrentBalances ? totalCurrent : undefined,
  };
};

/**
 * Validates if an account code is unique within the chart of accounts
 * @param accounts - Array of accounts to check against
 * @param accountCode - Code to validate
 * @param excludeId - ID to exclude from validation (for updates)
 * @returns True if code is unique, false otherwise
 */
export const isAccountCodeUnique = (
  accounts: IChartOfAccount[],
  accountCode: string,
  excludeId?: string
): boolean => {
  const flatAccounts = flattenAccounts(accounts);
  return !flatAccounts.some(
    (account) => account.accountCode === accountCode && account.id !== excludeId
  );
};

/**
 * Gets the next available account code for a given account type
 * @param accounts - Existing accounts array
 * @param accountType - Type of account (Asset, Liability, etc.)
 * @returns Next available account code
 */
export const getNextAccountCode = (
  accounts: IChartOfAccount[],
  accountType: string
): string => {
  const flatAccounts = flattenAccounts(accounts);

  // Define starting ranges for each account type
  const typeRanges: Record<string, number> = {
    Asset: 1000,
    Liability: 2000,
    Equity: 3000,
    Revenue: 4000,
    Expense: 5000,
  };

  const startRange = typeRanges[accountType] || 9000;
  const endRange = startRange + 999;

  // Find existing codes in the range
  const existingCodes = flatAccounts
    .filter((account) => account.accountType === accountType)
    .map((account) => parseInt(account.accountCode))
    .filter((code) => code >= startRange && code <= endRange)
    .sort((a, b) => a - b);

  // Find the first gap or next number
  for (let i = startRange; i <= endRange; i++) {
    if (!existingCodes.includes(i)) {
      return i.toString().padStart(4, "0");
    }
  }

  // If no gap found, return next number after the range
  return (endRange + 1).toString().padStart(4, "0");
};

/**
 * Filters accounts by type and active status
 * @param accounts - Array of accounts to filter
 * @param accountType - Type to filter by (optional)
 * @param activeOnly - Whether to include only active accounts
 * @returns Filtered accounts array
 */
export const filterAccounts = (
  accounts: IChartOfAccount[],
  accountType?: string,
  activeOnly: boolean = true
): IChartOfAccount[] => {
  const flatAccounts = flattenAccounts(accounts);

  return flatAccounts.filter((account) => {
    if (activeOnly && account.isActive === false) return false;
    if (accountType && account.accountType !== accountType) return false;
    return true;
  });
};

/**
 * Gets posting accounts (accounts that allow direct posting)
 * @param accounts - Array of accounts
 * @returns Array of accounts that allow direct posting
 */
export const getPostingAccounts = (
  accounts: IChartOfAccount[]
): IChartOfAccount[] => {
  const flatAccounts = flattenAccounts(accounts);
  return flatAccounts.filter(
    (account) => account.allowDirectPosting && account.isActive !== false
  );
};

/**
 * Builds account hierarchy display string (e.g., "Assets > Current Assets > Cash")
 * @param account - Account to build hierarchy for
 * @param allAccounts - All accounts for parent lookup
 * @returns Hierarchy string
 */
export const buildAccountHierarchy = (
  account: IChartOfAccount,
  allAccounts: IChartOfAccount[]
): string => {
  const hierarchy: string[] = [];
  let currentAccount: IChartOfAccount | null = account;

  // Pre-flatten accounts to avoid repeated flattening in the loop
  const flatAccounts = flattenAccounts(allAccounts);

  // Build hierarchy from bottom to top
  while (currentAccount) {
    hierarchy.unshift(currentAccount.accountName);

    if (currentAccount.parentAccountId) {
      const parentId: string = currentAccount.parentAccountId;
      currentAccount = flatAccounts.find((acc) => acc.id === parentId) || null;
    } else {
      break;
    }
  }

  return hierarchy.join(" > ");
};
